import { useEffect, useState } from 'react';

export const checkedImagesCache = new Map();

const isFailedResponse = response => {
  const contentType = response?.headers?.get?.('content-type') || '';

  return (
    response?.status === 404 ||
    response?.status === 303 ||
    (!!contentType && !contentType.startsWith('image/'))
  );
};

const checkWithImageLoader = src =>
  new Promise(resolve => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(false);
    img.onerror = () => resolve(true);
  });

const checkImageFailed = async src => {
  try {
    return isFailedResponse(await fetch(src, { method: 'HEAD' }));
  } catch {
    // HEAD is blocked or failed, try GET
  }

  try {
    return isFailedResponse(await fetch(src));
  } catch {
    // fetch failed (CORS), use standard Image load test
    return checkWithImageLoader(src);
  }
};

const resolveImageSrc = imageOverride =>
  decodeURI(imageOverride) !== imageOverride ? imageOverride : encodeURI(imageOverride);

export const useImageValidation = imageOverride => {
  const [failedOverride, setFailedOverride] = useState(false);

  useEffect(() => {
    let isMounted = true;

    if (!imageOverride) {
      setFailedOverride(false);
      return;
    }

    const src = resolveImageSrc(imageOverride);

    const applyResult = hasFailed => {
      if (isMounted) {
        setFailedOverride(hasFailed);
      }
    };

    const cached = checkedImagesCache.get(src);
    if (cached !== undefined) {
      if (typeof cached === 'boolean') {
        applyResult(cached);
      } else {
        cached.then(applyResult);
      }
      return;
    }

    setFailedOverride(false);

    const checkPromise = checkImageFailed(src).then(failed => {
      checkedImagesCache.set(src, failed);
      return failed;
    });

    checkedImagesCache.set(src, checkPromise);
    checkPromise.then(applyResult);

    return () => {
      isMounted = false;
    };
  }, [imageOverride]);

  return failedOverride;
};
